import { Client } from './client';
import { World } from './world';
import { DealType, Fives, GameType, GAME_TYPES, Points } from './types';
import { DEALS } from './setup-deal';
import { ensurePwaManifestLink } from './pwa-manifest';
import { getBasePath, getWsUrl } from './ws-url';
import type { BloodState, BloodSwap3Direction } from './blood';
import { syncProfileToGameClient } from './user-sync';
import { abandonActiveGame, ActiveGameInfo, ensureUser, fetchActiveGame, fetchMatchPointsInfo } from './user';

ensurePwaManifestLink();

type ElementMap = {
  create: HTMLButtonElement;
  status: HTMLElement;
  waitMode: HTMLSelectElement;
  roomBlock: HTMLElement;
  gameId: HTMLElement;
  sharePath: HTMLInputElement;
  copy: HTMLButtonElement;
  controls: HTMLElement;
  deal: HTMLButtonElement;
  resetPoints: HTMLButtonElement;
  toggleDealer: HTMLButtonElement;
  toggleHonba: HTMLButtonElement;
  dealType: HTMLSelectElement;
  gameType: HTMLSelectElement;
  fives: HTMLSelectElement;
  points: HTMLSelectElement;
};

class NullObjectView {
  replaceThings(_params: Map<number, any>): void {}
  replaceShadows(_places: any[]): void {}
  updateScores(_scores: Array<number | null>): void {}
  updateThings(_things: any[]): void {}
  updateDropShadows(_places: any[]): void {}
}

class NullSoundPlayer {
  play(_type: any, _side: any): void {}
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // fallback
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

const A2HS_DISMISS_KEY = 'mjlab_a2hs_dismissed_v1';

function isStandaloneMode(): boolean {
  const anyNav = navigator as any;
  if (typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(display-mode: standalone)').matches) return true;
    } catch {
      // ignore
    }
  }
  return anyNav?.standalone === true;
}

function isIosDevice(): boolean {
  const ua = navigator.userAgent ?? '';
  const isIpadDesktopUa = /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  return /iPhone|iPad|iPod/i.test(ua) || isIpadDesktopUa;
}

function shouldShowA2hsTip(): boolean {
  if (!isIosDevice()) return false;
  if (isStandaloneMode()) return false;
  try {
    return localStorage.getItem(A2HS_DISMISS_KEY) !== '1';
  } catch {
    return true;
  }
}

function dismissA2hsTip(): void {
  try {
    localStorage.setItem(A2HS_DISMISS_KEY, '1');
  } catch {
    // ignore
  }
}

function buildUi(root: HTMLElement): ElementMap {
  root.innerHTML = '';

  if (shouldShowA2hsTip()) {
    const card = el('div', 'alert alert-warning text-dark small mb-3', '');
    const title = el('div', 'font-weight-bold', 'iPhone 建议“添加到主屏幕”获得更沉浸（近全屏）体验');
    card.appendChild(title);
    card.appendChild(el('div', 'mt-1', '操作：点击浏览器“分享/菜单” → “添加到主屏幕”，然后从桌面图标进入。'));
    const actions = el('div', 'mt-2', '');
    const dismissBtn = el('button', 'btn btn-sm btn-outline-dark', '不再提示') as HTMLButtonElement;
    dismissBtn.type = 'button';
    dismissBtn.onclick = () => {
      dismissA2hsTip();
      card.remove();
    };
    actions.appendChild(dismissBtn);
    card.appendChild(actions);
    root.appendChild(card);
  }

  root.appendChild(el('h4', 'mb-3', '创建房间（移动端）'));

  const waitMode = el('select', 'form-control form-control-sm dark-select mb-2') as HTMLSelectElement;
  waitMode.appendChild(new Option('等待模式：玩家点击过（不限时）', 'noTimeout', true, true));
  waitMode.appendChild(new Option('等待模式：只超时自动（6 分钟）', 'timeoutAuto'));
  root.appendChild(waitMode);

  const create = el('button', 'btn btn-warning text-dark w-100', '创建房间') as HTMLButtonElement;
  root.appendChild(create);

  const status = el('div', 'mt-3 text-white-50', '');
  root.appendChild(status);

  const roomBlock = el('div', 'mt-3', '');
  roomBlock.style.display = 'none';
  root.appendChild(roomBlock);

  const gameIdLine = el('div', 'mb-2', '');
  gameIdLine.appendChild(el('span', 'text-white-50 mr-2', '房间号:'));
  const gameId = el('span', 'text-light font-weight-bold', '-') as HTMLElement;
  gameIdLine.appendChild(gameId);
  roomBlock.appendChild(gameIdLine);

  const shareRow = el('div', 'input-group mb-2', '');
  const sharePath = el('input', 'form-control', '') as HTMLInputElement;
  sharePath.readOnly = true;
  const copy = el('button', 'btn btn-outline-light', '复制链接') as HTMLButtonElement;
  const append = el('div', 'input-group-append', '');
  append.appendChild(copy);
  shareRow.appendChild(sharePath);
  shareRow.appendChild(append);
  roomBlock.appendChild(shareRow);

  const controls = el('div', 'mt-4', '');
  controls.style.display = 'none';
  roomBlock.appendChild(controls);

  controls.appendChild(el('h5', 'mb-2', '对局 + 设置（仅房主）'));

  const selects = el('div', 'row', '');
  controls.appendChild(selects);

  const col = (label: string, control: HTMLElement): HTMLElement => {
    const c = el('div', 'col-6 mb-2', '');
    c.appendChild(el('div', 'text-white-50 small mb-1', label));
    c.appendChild(control);
    return c;
  };

  const dealType = el('select', 'form-control form-control-sm dark-select') as HTMLSelectElement;
  dealType.appendChild(new Option('Deal: Hands', DealType.HANDS, true, true));
  dealType.appendChild(new Option('Deal: Only walls', DealType.INITIAL));
  dealType.appendChild(new Option('Deal: Winds', DealType.WINDS));
  dealType.appendChild(new Option('Deal: Only walls (not shuffled)', DealType.UNSHUFFLED));

  const gameType = el('select', 'form-control form-control-sm dark-select') as HTMLSelectElement;
  gameType.appendChild(new Option('血战到底', GameType.BLOOD_BATTLE, true, true));
  gameType.appendChild(new Option('4-player', GameType.FOUR_PLAYER));
  gameType.appendChild(new Option('3-player', GameType.THREE_PLAYER));
  gameType.appendChild(new Option('2-player Minefield', GameType.MINEFIELD));
  gameType.appendChild(new Option('2-player Bamboo', GameType.BAMBOO));
  gameType.appendChild(new Option('Demo', GameType.FOUR_PLAYER_DEMO));

  const fives = el('select', 'form-control form-control-sm dark-select') as HTMLSelectElement;
  fives.appendChild(new Option('No red fives', '000'));
  fives.appendChild(new Option('1-1-1', '111', true, true));
  fives.appendChild(new Option('1-2-1', '121'));

  const points = el('select', 'form-control form-control-sm dark-select') as HTMLSelectElement;
  points.appendChild(new Option('25k', '25', true, true));
  points.appendChild(new Option('30k', '30'));
  points.appendChild(new Option('35k', '35'));
  points.appendChild(new Option('40k', '40'));
  points.appendChild(new Option('100k', '100'));

  selects.appendChild(col('Deal', dealType));
  selects.appendChild(col('Game type', gameType));
  selects.appendChild(col('Red fives', fives));
  selects.appendChild(col('Starting points', points));

  const btnRow1 = el('div', 'd-flex gap-2 mt-2', '');
  btnRow1.style.gap = '8px';
  const deal = el('button', 'btn btn-warning text-dark w-100', 'Deal') as HTMLButtonElement;
  const resetPoints = el('button', 'btn btn-outline-light w-100', 'Reset points') as HTMLButtonElement;
  btnRow1.appendChild(deal);
  btnRow1.appendChild(resetPoints);
  controls.appendChild(btnRow1);

  const btnRow2 = el('div', 'd-flex gap-2 mt-2', '');
  btnRow2.style.gap = '8px';
  const toggleDealer = el('button', 'btn btn-outline-light w-100', 'Dealer +1') as HTMLButtonElement;
  const toggleHonba = el('button', 'btn btn-outline-light w-100', 'Honba +1') as HTMLButtonElement;
  btnRow2.appendChild(toggleDealer);
  btnRow2.appendChild(toggleHonba);
  controls.appendChild(btnRow2);

  return {
    create,
    status,
    waitMode,
    roomBlock,
    gameId,
    sharePath,
    copy,
    controls,
    deal,
    resetPoints,
    toggleDealer,
    toggleHonba,
    dealType,
    gameType,
    fives,
    points,
  };
}

function updateDealVisibility(elements: ElementMap): void {
  const gameType = elements.gameType.value as GameType;
  for (const option of Array.from(elements.dealType.options)) {
    const dealType = option.value as DealType;
    option.hidden = DEALS[gameType][dealType] === undefined;
  }
  if (DEALS[gameType][elements.dealType.value as DealType] === undefined) {
    const first = Array.from(elements.dealType.options).find(o => !o.hidden);
    if (first) {
      elements.dealType.value = first.value;
    }
  }

  // 血战到底：固定 108 张序数牌，不使用红五/点棒设置
  const blood = gameType === GameType.BLOOD_BATTLE;
  elements.fives.disabled = blood;
  elements.points.disabled = blood;
  if (blood) {
    elements.fives.value = '000';
    elements.points.value = GAME_TYPES[gameType].points;
  }
}

function setControlsEnabled(elements: ElementMap, enabled: boolean): void {
  const controls: Array<HTMLButtonElement | HTMLSelectElement> = [
    elements.deal,
    elements.resetPoints,
    elements.toggleDealer,
    elements.toggleHonba,
    elements.dealType,
    elements.gameType,
    elements.fives,
    elements.points,
  ];
  for (const c of controls) {
    c.disabled = !enabled;
  }
}

const root = document.getElementById('mobile-root') as HTMLElement;
const elements = buildUi(root);

let client: Client | null = null;
let world: World | null = null;
let wantFriendConfig: { waitMode: 'noTimeout' | 'timeoutAuto'; timeoutMs?: number | null } | null = null;
let insufficientRedirectTimeoutId: number | null = null;
let insufficientRedirectIntervalId: number | null = null;

function waitModeLabel(mode: string | null | undefined): string {
  return mode === 'timeoutAuto' ? '只超时自动（6 分钟）' : '玩家点击过（不限时）';
}

function clearInsufficientRedirectTimers(): void {
  if (insufficientRedirectTimeoutId !== null) {
    window.clearTimeout(insufficientRedirectTimeoutId);
    insufficientRedirectTimeoutId = null;
  }
  if (insufficientRedirectIntervalId !== null) {
    window.clearInterval(insufficientRedirectIntervalId);
    insufficientRedirectIntervalId = null;
  }
}

function getPersonalCenterUrl(): string {
  return `${getBasePath()}me/`;
}

function normalizeActiveGame(raw: any): ActiveGameInfo | null {
  const gameId = typeof raw?.gameId === 'string' ? raw.gameId.trim() : '';
  if (!gameId) return null;
  const seatRaw = Number(raw?.seat);
  const seat = Number.isFinite(seatRaw) ? Math.trunc(seatRaw) : -1;
  if (seat < 0 || seat > 3) return null;
  const roomType = typeof raw?.roomType === 'string' ? raw.roomType.trim() || null : null;
  return { gameId, seat, roomType };
}

function modeLabelOfActiveGame(active: ActiveGameInfo): string {
  if (active.roomType === 'newbie') return '新手房';
  if (active.roomType === 'practice-branch') return '练习分支';
  return '好友房';
}

function activeGameUrl(active: ActiveGameInfo): string {
  const basePath = getBasePath();
  const room = active.roomType === 'newbie' ? '&room=newbie' : '';
  return `${basePath}hand/?gameId=${encodeURIComponent(active.gameId)}${room}`;
}

function showActiveGameGuard(active: ActiveGameInfo, onAbandonAndContinue?: () => void | Promise<void>): void {
  clearInsufficientRedirectTimers();
  elements.status.innerHTML = '';

  const msg = document.createElement('div');
  msg.textContent = `你已有进行中的${modeLabelOfActiveGame(active)}（座位 ${active.seat + 1}），暂不允许新开房间。`;
  elements.status.appendChild(msg);

  const row = document.createElement('div');
  row.className = 'mt-2';
  const continueBtn = document.createElement('button');
  continueBtn.className = 'btn btn-warning btn-sm text-dark';
  continueBtn.type = 'button';
  continueBtn.textContent = '继续旧牌局（推荐）';
  continueBtn.onclick = () => {
    window.location.href = activeGameUrl(active);
  };
  row.appendChild(continueBtn);

  const abandonBtn = document.createElement('button');
  abandonBtn.className = 'btn btn-outline-light btn-sm ms-2';
  abandonBtn.type = 'button';
  abandonBtn.textContent = '放弃旧牌局并新开';
  let abandoning = false;
  abandonBtn.onclick = async () => {
    if (abandoning) return;
    abandoning = true;
    continueBtn.disabled = true;
    abandonBtn.disabled = true;
    abandonBtn.textContent = '处理中...';
    try {
      const out = await abandonActiveGame(active.gameId);
      const remaining = normalizeActiveGame((out as any)?.remainingActiveGame);
      if (remaining) {
        abandoning = false;
        showActiveGameGuard(remaining, onAbandonAndContinue);
        return;
      }
      elements.status.textContent = '已放弃旧牌局，正在继续新开流程…';
      if (onAbandonAndContinue) {
        await onAbandonAndContinue();
      }
    } catch (err: unknown) {
      elements.status.textContent = `放弃旧牌局失败：${String((err as any)?.message ?? err)}`;
      continueBtn.disabled = false;
      abandonBtn.disabled = false;
      abandonBtn.textContent = '放弃旧牌局并新开';
      abandoning = false;
      return;
    }
    abandoning = false;
  };
  row.appendChild(abandonBtn);
  elements.status.appendChild(row);
}

function showInsufficientStatusAndRedirect(params: { canClaimDaily: boolean; dailyClaimAmount: number }): void {
  clearInsufficientRedirectTimers();
  let remain = 3;
  const amount = Math.max(0, Math.trunc(params.dailyClaimAmount ?? 4000));
  const text = params.canClaimDaily
    ? `对局积分不足。今日可领取 ${amount} 对局积分，3 秒后将跳转个人中心。`
    : '对局积分不足。今日领取次数已用完，3 秒后将跳转个人中心。';

  elements.status.innerHTML = '';
  const msg = document.createElement('div');
  msg.textContent = text;
  elements.status.appendChild(msg);

  const countdown = document.createElement('div');
  countdown.style.marginTop = '6px';
  const renderCountdown = (): void => {
    countdown.textContent = `${remain} 秒后自动跳转到个人中心。`;
  };
  renderCountdown();
  elements.status.appendChild(countdown);

  const goNowBtn = document.createElement('button');
  goNowBtn.className = 'btn btn-outline-light btn-sm mt-2';
  goNowBtn.type = 'button';
  goNowBtn.textContent = '立即前往个人中心';
  goNowBtn.onclick = () => {
    clearInsufficientRedirectTimers();
    window.location.href = getPersonalCenterUrl();
  };
  elements.status.appendChild(goNowBtn);

  insufficientRedirectTimeoutId = window.setTimeout(() => {
    clearInsufficientRedirectTimers();
    window.location.href = getPersonalCenterUrl();
  }, 3000);
  insufficientRedirectIntervalId = window.setInterval(() => {
    remain -= 1;
    if (remain <= 0) {
      clearInsufficientRedirectTimers();
      return;
    }
    renderCountdown();
  }, 1000);
}

elements.gameType.onchange = () => {
  updateDealVisibility(elements);
  const gameType = elements.gameType.value as GameType;
  elements.points.value = GAME_TYPES[gameType].points;
};
elements.dealType.onchange = () => updateDealVisibility(elements);
updateDealVisibility(elements);

const createRoom = async (): Promise<void> => {
  if (client?.connected()) {
    return;
  }
  clearInsufficientRedirectTimers();

  let hostStartBeans = 0;
  try {
    await ensureUser();
    const pointsInfo = await fetchMatchPointsInfo();
    hostStartBeans = Math.max(0, Math.trunc(pointsInfo.matchPoints ?? 0));
    if (hostStartBeans <= 0) {
      showInsufficientStatusAndRedirect({
        canClaimDaily: !!pointsInfo.canClaimDaily,
        dailyClaimAmount: Math.max(0, Math.trunc(pointsInfo.dailyClaimAmount ?? 4000)),
      });
      return;
    }
    try {
      const active = normalizeActiveGame((await fetchActiveGame()).activeGame);
      if (active) {
        showActiveGameGuard(active, async () => {
          await createRoom();
        });
        return;
      }
    } catch {
      // ignore
    }
  } catch {
    elements.status.textContent = '账号校验失败，请稍后重试。';
    return;
  }

  elements.waitMode.disabled = true;
  elements.status.textContent = '正在创建房间并连接服务器…';
  elements.create.disabled = true;
  wantFriendConfig = {
    waitMode: elements.waitMode.value === 'timeoutAuto' ? 'timeoutAuto' : 'noTimeout',
    timeoutMs: elements.waitMode.value === 'timeoutAuto' ? 6 * 60 * 1000 : null,
  };

  client = new Client();
  world = new World(new NullObjectView(), new NullSoundPlayer(), client);

  client.on('connect', (game, isFirst) => {
    // Sync nickname/avatar from user profile (if logged in on this device).
    void syncProfileToGameClient(client!);
    // 预占 seat=0，保证房主恒为 seat0
    client!.seats.set(client!.playerId(), { seat: 0 });

    const basePath = getBasePath();
    const path = `${basePath}hand/?gameId=${game.gameId}`;
    const fullUrl = new URL(path, window.location.origin).toString();

    elements.roomBlock.style.display = 'block';
    const authoritative = client!.isAuthoritative();
    elements.controls.style.display = authoritative ? 'none' : 'block';
    elements.gameId.textContent = game.gameId;
    elements.sharePath.value = path;
    elements.status.textContent = authoritative
      ? `已创建房间（服务端权威模式）。等待模式已生效：${waitModeLabel(wantFriendConfig?.waitMode)}。分享链接给其他玩家进入 /hand/。`
      : '已创建房间。分享链接给其他玩家进入 /hand/。';

    elements.copy.onclick = async () => {
      const ok = await copyToClipboard(fullUrl);
      elements.status.textContent = ok ? '已复制链接。' : '复制失败，请手动复制。';
    };

    // 创建房间后自动发一局（默认 Hands）
    if (isFirst && world && !client!.isAuthoritative()) {
      const dealType = DealType.HANDS;
      const gameType = elements.gameType.value as GameType;
      const fives = (gameType === GameType.BLOOD_BATTLE ? '000' : elements.fives.value) as Fives;
      const points = elements.points.value as Points;
      world.deal(dealType, gameType, fives, points);

      if (gameType === GameType.BLOOD_BATTLE) {
        const startBySeat: Record<number, number> = {
          0: hostStartBeans,
          1: 0,
          2: 0,
          3: 0,
        };
        for (const [pid, seatInfo] of client!.seats.entries()) {
          const seat = seatInfo?.seat ?? null;
          if (seat === null || seat === undefined) continue;
          if (seat < 0 || seat > 3) continue;
          const raw = (seatInfo as any)?.startBeans;
          const beans = Number.isFinite(raw) ? Math.max(0, Math.trunc(raw as number)) : null;
          if (beans === null) continue;
          startBySeat[seat] = beans;
        }
        const emptyPlayer = (seat: number, playerId: string | null) => ({
          seat,
          playerId,
          dingque: null,
          hu: false,
          huTileKey: null,
          huSource: null,
          beans: startBySeat[seat] ?? 0,
          kongGain: 0,
          melds: [],
        });

        const swapDirs: Array<BloodSwap3Direction> = ['cw', 'ccw', 'across'];
        const swapDir = swapDirs[Math.floor(Math.random() * swapDirs.length)] ?? 'cw';

        const state: BloodState = {
          version: 1,
          base: 400,
          initialBeans: startBySeat[0] ?? 0,
          initialBeansBySeat: startBySeat,
          phase: 'swap3',
          dealer: 0,
          turnSeat: 0,
          turnStep: 'discard',
          wallOrder: [],
          wallIndex: 0,
          nextId: 1,
          pending: null,
          swap3: {
            dir: swapDir,
            since: Date.now(),
            animatingSince: null,
            selections: { 0: null, 1: null, 2: null, 3: null },
          },
          ledger: [],
          players: {
            0: emptyPlayer(0, client!.playerId()),
            1: emptyPlayer(1, null),
            2: emptyPlayer(2, null),
            3: emptyPlayer(3, null),
          },
          revealAllHands: false,
          settlingSince: null,
        };

        client!.blood.set(0, state);
      }
    }
  });

  client.on('disconnect', () => {
    elements.status.textContent = '连接已断开。请刷新或重新创建房间。';
    elements.waitMode.disabled = false;
    setControlsEnabled(elements, false);
  });

  client.on('friendConfigSetAck', (ack) => {
    if (!client || !client.connected()) return;
    if (!ack.ok) {
      elements.status.textContent = `等待模式设置失败：${ack.error ?? 'unknown error'}`;
      return;
    }
    const mode = (ack.config as any)?.waitMode ?? null;
    elements.status.textContent = `已创建房间（服务端权威模式）。等待模式已生效：${waitModeLabel(mode)}。分享链接给其他玩家进入 /hand/。`;
  });

  client.seats.on('update', () => {
    const authoritative = client!.isAuthoritative();
    const isHost = client!.seat === 0;
    setControlsEnabled(elements, !authoritative && isHost);
    if (authoritative) {
      return;
    }
    if (!isHost && client!.connected()) {
      elements.status.textContent = '仅房主（seat=0）可在此页操作对局设置。';
    }
  });

  elements.deal.onclick = () => {
    if (!client?.connected() || !world) {
      return;
    }
    if (client.isAuthoritative()) {
      return;
    }
    const dealType = elements.dealType.value as DealType;
    const gameType = elements.gameType.value as GameType;
    const fives = elements.fives.value as Fives;
    const points = elements.points.value as Points;
    world.deal(dealType, gameType, fives, points);
  };
  elements.resetPoints.onclick = () => {
    if (!client?.connected() || !world) {
      return;
    }
    if (client.isAuthoritative()) {
      return;
    }
    const points = elements.points.value as Points;
    world.resetPoints(points);
  };
  elements.toggleDealer.onclick = () => {
    if (client?.isAuthoritative()) return;
    world?.toggleDealer();
  };
  elements.toggleHonba.onclick = () => {
    if (client?.isAuthoritative()) return;
    world?.toggleHonba();
  };

  setControlsEnabled(elements, false);
  client.new(getWsUrl(), { friendConfig: wantFriendConfig ?? undefined });
};

elements.create.onclick = () => {
  void createRoom();
};
